/**
 * Request utilities: deduplication + retry with exponential backoff.
 *
 * - dedup: prevents duplicate concurrent requests for the same key
 * - withRetry: retries transient failures with exponential backoff + jitter
 */

// ─── In-flight Request Deduplication ─────────────────────────

const inflightRequests = new Map<string, Promise<unknown>>();

/**
 * Deduplicate concurrent identical requests.
 * If a request with the same key is already in-flight, returns the existing promise.
 * The cached promise is removed once it settles (resolves or rejects).
 */
export function dedup<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const existing = inflightRequests.get(key) as Promise<T> | undefined;
  if (existing) return existing;

  const promise = fn().finally(() => {
    inflightRequests.delete(key);
  });
  inflightRequests.set(key, promise);
  return promise;
}

// ─── Retry with Exponential Backoff ──────────────────────────

interface RetryOptions {
  maxRetries?: number;
  baseDelayMs?: number;
  /** Only retry if this returns true for the error. Defaults to retrying network/timeout errors. */
  shouldRetry?: (error: unknown) => boolean;
}

function isTransientError(error: unknown): boolean {
  if (!navigator.onLine) return true;
  if (error instanceof TypeError) return true; // network errors
  if (error instanceof Error && error.message.includes('Failed to fetch')) return true;
  if (error instanceof Error && error.message.includes('timeout')) return true;
  // Treat HTTP 429 (rate limit) as transient — retry after backoff
  if (error instanceof Response && error.status === 429) return true;
  if (error instanceof Error && error.message.includes('429')) return true;
  return false;
}

/**
 * Retry a function with exponential backoff and jitter.
 * Only retries transient errors (network failures, timeouts) by default.
 */
export async function withRetry<T>(fn: () => Promise<T>, options: RetryOptions = {}): Promise<T> {
  const { maxRetries = 2, baseDelayMs = 500, shouldRetry = isTransientError } = options;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      if (attempt === maxRetries || !shouldRetry(err)) throw err;
      const delay = baseDelayMs * Math.pow(2, attempt) + Math.random() * 200;
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  // Unreachable, but TypeScript needs it
  throw new Error('Retry exhausted');
}

/**
 * Race `promise` against a timer. If the timer wins, resolve with `fallback`
 * and leave the original work running (callers like suggest-next-words must
 * not hang the UI when an edge function never returns).
 *
 * Rejects still propagate — wrap the caller in try/catch if you want a
 * fallback on error too. Rejected alternative: aborting the in-flight
 * invoke, which supabase-js does not expose cleanly.
 */
export function withTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<T>((resolve) => {
    timer = setTimeout(() => resolve(fallback), ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    if (timer !== undefined) clearTimeout(timer);
  });
}
